import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { type ColumnDef, type SortingState } from "@tanstack/react-table"
import { Plus, Search, Package } from "lucide-react"
import { ExportButton } from "@/components/export-button"
import { useProducts, useProductCategories } from "@/hooks/use-products"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useFilterParams } from "@/hooks/use-filter-params"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/data-table"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { SortableHeader } from "@/components/sortable-header"
import { FilterPanel, SelectFilter } from "@/components/filters"
import { Badge } from "@/components/ui/badge"
import { formatCurrency, sortingStateToParam } from "@/lib/utils"
import type { Product } from "@/lib/types"

const BASE_COLUMNS: ColumnDef<Product, unknown>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column}>Nazwa</SortableHeader>,
    cell: ({ row }) => (
      <div>
        <div className="font-medium text-foreground">{row.original.name}</div>
        {row.original.sku && (
          <div className="text-xs font-mono text-muted-foreground">
            {row.original.sku}
          </div>
        )}
      </div>
    ),
  },
  {
    accessorKey: "category",
    header: "Kategoria",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.category ? (
        <span className="text-sm">{row.original.category.name}</span>
      ) : (
        <span className="text-muted-foreground">{"\u2014"}</span>
      ),
  },
  {
    accessorKey: "net_price",
    header: ({ column }) => <SortableHeader column={column}>Cena netto</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-sm font-mono tabular-nums">
        {row.original.net_price != null ? formatCurrency(row.original.net_price) : "\u2014"}
      </span>
    ),
  },
  {
    accessorKey: "vat_rate",
    header: ({ column }) => <SortableHeader column={column}>VAT</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-sm tabular-nums">
        {row.original.vat_rate != null ? `${row.original.vat_rate}%` : "\u2014"}
      </span>
    ),
  },
  {
    accessorKey: "gross_price",
    header: ({ column }) => <SortableHeader column={column}>Cena brutto</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-sm font-mono tabular-nums font-medium">
        {row.original.gross_price != null ? formatCurrency(row.original.gross_price) : "\u2014"}
      </span>
    ),
  },
  {
    accessorKey: "unit",
    header: ({ column }) => <SortableHeader column={column}>Jednostka</SortableHeader>,
    cell: ({ row }) => <span className="text-sm">{row.original.unit}</span>,
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
    cell: ({ row }) => (
      <Badge variant={row.original.is_active ? "outline" : "secondary"} className="text-xs">
        {row.original.is_active ? "Aktywny" : "Nieaktywny"}
      </Badge>
    ),
  },
]

const PRODUCT_FILTER_KEYS = ["is_active", "category_id"] as const

const DEFAULT_VISIBLE = ["name", "net_price", "vat_rate", "gross_price", "unit", "is_active"]
const ALL_COLUMN_IDS = ["name", "category", "net_price", "vat_rate", "gross_price", "unit", "is_active"]

const COLUMN_LABELS: Record<string, string> = {
  name: "Nazwa",
  category: "Kategoria",
  net_price: "Cena netto",
  vat_rate: "VAT",
  gross_price: "Cena brutto",
  unit: "Jednostka",
  is_active: "Status",
}

const columnOptions: ColumnOption[] = ALL_COLUMN_IDS.map((id) => ({
  id,
  label: COLUMN_LABELS[id],
}))

export function ProductsListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sorting, setSorting] = useState<SortingState>([])
  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(PRODUCT_FILTER_KEYS)

  const { data: categoriesData } = useProductCategories()
  const categories = categoriesData?.data ?? []

  const { data, isLoading } = useProducts({
    search: debouncedSearch,
    sort: sortingStateToParam(sorting),
    ...filters,
  })

  const {
    columnVisibility,
    setColumnVisibility,
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("products", ALL_COLUMN_IDS, DEFAULT_VISIBLE)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    clearTimeout((window as unknown as Record<string, unknown>).__productSearchTimeout as number)
    ;(window as unknown as Record<string, unknown>).__productSearchTimeout = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={20} className="text-primary" />
          <h1 className="page-title">
            {t("products.title")}
          </h1>
          {data && (
            <span className="text-sm text-muted-foreground">
              ({data.data.length}{data.meta.has_more ? "+" : ""})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="products"
            filters={{ search: debouncedSearch, ...filters }}
            visibleColumns={Object.keys(columnVisibility).filter((k) => columnVisibility[k] !== false)}
          />
          <Button onClick={() => navigate("/products/new")}>
            <Plus size={16} />
            {t("products.new")}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t("common.search")}
            className="h-9 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ColumnSelector
          columns={columnOptions}
          visibleCount={visibleCount}
          totalCount={totalCount}
          isColumnVisible={isColumnVisible}
          onToggleColumn={toggleColumn}
          onShowAll={showAll}
          onHideAll={hideAll}
        />
      </div>

      {/* Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <SelectFilter
          label="Status"
          value={filters.is_active || ""}
          onChange={(v) => setFilter("is_active", v)}
          options={[
            { value: "true", label: "Aktywny" },
            { value: "false", label: "Nieaktywny" },
          ]}
          placeholder="Wszystkie"
        />
        <SelectFilter
          label="Kategoria"
          value={filters.category_id || ""}
          onChange={(v) => setFilter("category_id", v)}
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="Wszystkie kategorie"
        />
      </FilterPanel>

      <DataTable
        columns={BASE_COLUMNS}
        data={data?.data ?? []}
        isLoading={isLoading}
        onRowClick={(product) => navigate(`/products/${product.id}`)}
        emptyMessage={t("common.noResults")}
        sorting={sorting}
        onSortingChange={setSorting}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />
    </div>
  )
}
